/* eslint-disable max-statements */

import { Autocomplete, AutocompleteProps, Loader } from '@mantine/core';
import { useDebouncedCallback } from '@mantine/hooks';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { DEBOUNCE_TIME } from '@constants';

export type AutocompleteItemType = string | { value?: string; label?: string; [key: string]: any };

export interface AsyncAutocompleteProps extends Omit<AutocompleteProps, 'data'> {
  fetchOptions?: (query: string) => Promise<AutocompleteItemType[]>;
  data?: AutocompleteItemType[];
  debounceTime?: number;
  minChars?: number;
  initialOptions?: AutocompleteItemType[];
  errorMessage?: string;
  'data-pw'?: string;
}

export const AsyncAutocomplete: React.FC<AsyncAutocompleteProps> = ({
  fetchOptions,
  data = [],
  debounceTime = DEBOUNCE_TIME,
  minChars = 1,
  initialOptions = [],
  errorMessage = 'Failed to load options',
  onChange,
  onFocus,
  onBlur,
  ...props
}) => {
  const [focused, setFocused] = useState(false);
  const [query, setQuery] = useState(props.value ?? '');

  const displayedValue = useRef(props.value ?? '');
  // True while the input shows a raw code that came from props.value: its label is unknown until the
  // options describing it are loaded. Never true for text the user typed.
  const awaitingLabel = useRef(!!props.value);
  // Last term actually sent to fetchOptions, so swapping a code for its label - which changes the
  // displayed text but not the search - does not fire the very same request twice.
  const lastSearch = useRef<string | null>(null);
  const [options, setOptions] = useState<AutocompleteItemType[]>(fetchOptions ? initialOptions : data);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const floating = useMemo(() => {
    const value = ['number', 'string'].includes(typeof query) ? String(query).trim() : '';
    return value.length !== 0 || focused ? true : undefined;
  }, [query, focused]);

  const getItemLabel = (item: AutocompleteItemType): string => {
    if (typeof item === 'string') {
      return item;
    }
    return item.label;
  };

  const getItemValue = (item: AutocompleteItemType): string => {
    if (typeof item === 'string') {
      return item;
    }
    return item.value;
  };

  const filterItems = (items: AutocompleteItemType[], searchQuery: string): AutocompleteItemType[] => {
    const lowercaseQuery = searchQuery.toLowerCase();
    return items.filter((item) => {
      const label = getItemLabel(item);
      return label.toLowerCase().includes(lowercaseQuery);
    });
  };

  const debouncedQuery = useDebouncedCallback(async (query: string) => {
    if (!fetchOptions || query === lastSearch.current) {
      return;
    }

    lastSearch.current = query;
    setLoading(true);
    setError(null);

    try {
      const result = await fetchOptions(query);
      setOptions(result);
    } catch (err) {
      setError(errorMessage);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, debounceTime);

  // Update options when data prop changes (for non-async mode)
  useEffect(() => {
    if (!fetchOptions) {
      setOptions(data);
    }
  }, [data, fetchOptions]);

  useEffect(() => {
    setError(props.error as string);
  }, [props.error]);

  // Whether the text on screen is an option's label rather than the raw value it stands for.
  const showsLabel = !!displayedValue.current && query !== displayedValue.current;

  /* if the text on input is a label and we try to type a value, erase the label */
  const textAfterLabelEdit = (text: string): string => {
    if (!showsLabel) {
      return text;
    }

    if (text.length < query.length && query.startsWith(text)) {
      return '';
    }

    if (text.length > query.length && text.startsWith(query)) {
      return text.slice(query.length);
    }

    return text;
  };

  const handleChange = (rawText: string) => {
    const text = textAfterLabelEdit(rawText);

    // Options are rendered by label, so an option submit arrives here as a label: display it as-is,
    // but resolve it to the code before handing it to the consumer.
    const selectedItem = options.find((item) => getItemLabel(item) === text);
    const resolvedValue = selectedItem && typeof selectedItem !== 'string' ? selectedItem.value : text;

    displayedValue.current = resolvedValue;
    awaitingLabel.current = false;
    setQuery(text);

    if (!fetchOptions) {
      const filteredOptions = filterItems(data, text);
      setOptions(filteredOptions);
    }

    onChange?.(resolvedValue);
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    // If no query and in async mode with initial options, load them
    if (fetchOptions && !query && initialOptions.length > 0) {
      setOptions(initialOptions);
    }

    // In non-async mode, restore all options when focusing with empty query
    if (!fetchOptions && !query) {
      setOptions(data);
    }

    onFocus?.(event);
    setFocused(true);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    onBlur?.(event);
    setFocused(false);
  };

  useEffect(() => {
    if (props.value === undefined || props.value === displayedValue.current) {
      // Already displaying this code (as its label, after a selection) - keep the label on screen.
      return;
    }

    // props.value came from outside (initial load, form reset, a sibling field): its label is not
    // known here, so fall back to showing the code itself.
    displayedValue.current = props.value;
    awaitingLabel.current = true;
    setQuery(props.value);
  }, [props.value]);

  useEffect(() => {
    if (!awaitingLabel.current || !displayedValue.current) {
      return;
    }

    const selectedItem = options.find((item) => getItemValue(item) === displayedValue.current);
    const label = selectedItem ? getItemLabel(selectedItem) : undefined;

    if (label && label !== query) {
      awaitingLabel.current = false;
      setQuery(label);
    }
  }, [options]);

  useEffect(() => {
    if (focused) {
      // Search by the code rather than by the label sitting in the input, so re-opening the dropdown
      // after a selection still matches the option that is currently selected.
      debouncedQuery(displayedValue.current || '');
    }
  }, [query, focused]);

  const processedData = options.map((item) => {
    if (typeof item === 'string') {
      return item;
    }
    return item.label;
  });

  return (
    <Autocomplete
      data-pw={props['data-pw']}
      {...props}
      value={query}
      data={processedData}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      rightSection={loading ? <Loader size="xs" /> : props.rightSection}
      error={error}
      data-floating={floating}
      labelProps={{ ...props.labelProps, 'data-floating': floating, 'data-error': error }}
    />
  );
};
